import { DatePicker, Input, InputNumber, message, Select, Switch, Tag, Button } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'
import type { BacktestFormData } from '@/types'
import './index.scss'
import { sectorOptions, benchmarkOptions, DEFAULT_BENCHMARK } from '@/pages/static'
import ContactPool from '@/components/ContactPool'

type SignalConfig = {
    key: string
    title: string
    description: string
}

export type SecondStepValue = BacktestFormData

type SecondStepProps = {
    value?: BacktestFormData
    onChange?: (value: BacktestFormData) => void
}

const signalOptions: SignalConfig[] = [
    {
        key: 'includeStockSelectionSignal',
        title: '接收候选股票信号',
        description: '接收策略助手或 Agent 输出的股票池建议，作为初始筛选条件。',
    },
    {
        key: 'includeBuySellSignal',
        title: '接收买卖决策信号',
        description: '接受策略助手给出的买卖信号，评估后参与仓位生成参数。',
    },
    {
        key: 'includeMacroSignal',
        title: '启用宏观环境接入',
        description: '结合市场风格、波动率与宏观信号，对仓位与节奏进行动态调整。',
    },
]

export function createSecondStepDefaultValue(): BacktestFormData {
    return {
        display_name: `量化回测_${dayjs().format('YYYY-MM-DD')}`,
        benchmark: DEFAULT_BENCHMARK,
        start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD'),
        freq: '日K线',
        cash: 1000000,
        max_positions: 20,
        slippage: 0.1,
        position_sizing: '等权分配',
        commission: 0.03,
        market: 'HK',
        pre_market: 'HK',
        signals: {
            includeStockSelectionSignal: true,
            includeBuySellSignal: true,
            includeMacroSignal: false,
        },
        version_no: '',
        version_name: '',
        focus_sectors: [],
        constraints: [],
        stock_pool_id: null,
        stock_pool_name: '',
    }
}

export default function SecondStep({ value, onChange }: SecondStepProps) {
    const [formData, setFormData] = useState<BacktestFormData>(value ?? createSecondStepDefaultValue())
    const [visible, setVisible] = useState(false)
    
    useEffect(() => {
        if (!value) {
            return
        }
        // 历史草稿可能仍带已下线的「30分钟」，归一到 60分钟
        if (value.freq === '30分钟') {
            setFormData({ ...value, freq: '60分钟' })
            return
        }
        setFormData(value)
    }, [value])

    useEffect(() => {
        onChange?.(formData)
    }, [formData, onChange])

    const updateField = <K extends keyof BacktestFormData>(key: K, fieldValue: BacktestFormData[K]) => {
        setFormData((prev) => ({
            ...prev,
            [key]: fieldValue,
        }))
    }

    const updateSignal = (key: string, checked: boolean) => {
        setFormData((prev) => ({
            ...prev,
            signals: {
                ...prev.signals,
                [key]: checked,
            },
        }))
    }

    const disableEndDate = (current: Dayjs) => {
        if (!current) {
            return false
        }

        const isAfterToday = current.isAfter(dayjs().endOf('day'))
        const isNotAfterStart = formData.start ? !current.isAfter(dayjs(formData.start), 'day') : false

        return isAfterToday || isNotAfterStart
    }

    const disableStartDate = (current: Dayjs) => {
        if (!current) {
            return false
        }

        const isAfterToday = current.isAfter(dayjs().endOf('day'))
        const isNotBeforeEnd = formData.end ? !current.isBefore(dayjs(formData.end), 'day') : false

        return isAfterToday || isNotBeforeEnd
    }

    const toggleSector = (sector: string, checked: boolean) => {
        setFormData((prev) => ({
            ...prev,
            focus_sectors: checked
                ? [...prev.focus_sectors, sector]
                : prev.focus_sectors.filter((item) => item !== sector),
        }))
    }

    const addConstraint = () => {
        setFormData((prev) => ({
            ...prev,
            constraints: [...prev.constraints, ''],
        }))
    }

    const updateConstraintDraft = (value: string, index: number) => {
        setFormData((prev) => ({
            ...prev,
            constraints: prev.constraints.map((item, itemIndex) => itemIndex === index ? value : item),
        }))
    }

    const changeConstraint = (value: string , index: number) => {
        const val = value.trim()
        setFormData((prev) => ({
            ...prev,
            constraints: (() => {
                if (!val) {
                    return prev.constraints.filter((_, itemIndex) => itemIndex !== index)
                }

                if (prev.constraints.some((item, itemIndex) => itemIndex !== index && item.trim() === val)) {
                    message.warning('该约束已存在')
                    return prev.constraints.map((item, itemIndex) => itemIndex === index ? '' : item)
                }

                return prev.constraints.map((item, itemIndex) => itemIndex === index ? val : item)
            })(),
        }))
    }

    const removeConstraint = (index: number) => {
        setFormData((prev) => ({
            ...prev,
            constraints: prev.constraints.filter((_, itemIndex) => itemIndex !== index),
        }))
    }

    const onContactPool = (poolId: number | null, poolName?: string | null) => {
        setFormData((prev) => ({
            ...prev,
            stock_pool_id: poolId,
            stock_pool_name: poolName ?? null,
        }))
        setVisible(false)
    }

    return (
        <section className="second-step">
            <div className="second-step__section">
                <div className="second-step__section-title font-weight-600">基本设置</div>
                <div className="second-step__section-base-info">
                    <div className='flex flex-between' style={{ marginBottom: 24,marginLeft: 24 }}>
                        <div>
                            策略快照 
                            <span className='font-weight-700' style={{ color: 'var(--accent)', marginInline: 8 }}>
                                {formData.version_no}
                            </span> 
                            <span className='color-2'>{formData.version_name}</span>
                        </div>
                        <Tag variant='filled' className='is-purple text-10'>{formData.pre_market}</Tag>
                    </div>
                    <div className="second-step__label">市场类型</div>
                    <div className='second-step__market-tags'>
                        <Tag className={`stock-tag ${formData.market === 'US' ? 'is-active' : ''}`} onClick={() => updateField('market', 'US')}>美股</Tag>
                        <Tag className={`stock-tag ${formData.market === 'HK' ? 'is-active' : ''}`} onClick={() => updateField('market', 'HK')}>港股</Tag>
                    </div>
                </div>
                <div className="second-step__grid second-step__grid--2">
                    <div className="second-step__field">
                        <div className="second-step__label">回测名称</div>
                        <Input
                            value={formData.display_name}
                            onChange={(event) => updateField('display_name', event.target.value)}
                            placeholder="请输入回测名称"
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">基准指数</div>
                        <Select
                            value={formData.benchmark}
                            onChange={(fieldValue) => updateField('benchmark', fieldValue)}
                            options={benchmarkOptions}
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">回测开始日期</div>
                        <DatePicker
                            value={formData.start ? dayjs(formData.start) : null}
                            onChange={(_, dateString) => updateField('start', typeof dateString === 'string' ? dateString : '')}
                            disabledDate={disableStartDate}
                            allowClear={false}
                            className="second-step__picker"
                            size='large'
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">回测结束日期</div>
                        <DatePicker
                            value={formData.end ? dayjs(formData.end) : null}
                            onChange={(_, dateString) => updateField('end', typeof dateString === 'string' ? dateString : '')}
                            disabledDate={disableEndDate}
                            allowClear={false}
                            className="second-step__picker"
                            size='large'
                        />
                    </div>
                </div>
            </div>

            <section className="second-step__section">
                <div className="second-step__section-title font-weight-600">回测参数</div>
                <div className="second-step__grid second-step__grid--3">
                    <div className="second-step__field">
                        <div className="second-step__label">K线级别</div>
                        <Select
                            value={formData.freq}
                            onChange={(fieldValue) => updateField('freq', fieldValue)}
                            options={[
                                { value: '日K线', label: '日K线' },
                                { value: '60分钟', label: '60分钟' },
                            ]}
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">初始资金（元）</div>
                        <Input
                            value={formData.cash}
                            onChange={(event) => updateField('cash', Number(event.target.value) || 0)}
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">最大持仓数</div>
                        <Input
                            value={formData.max_positions}
                            onChange={(event) => updateField('max_positions', Number(event.target.value))}
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">手续费基准</div>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={0.01}
                            suffix="%"
                            value={Number.isFinite(formData.commission) ? formData.commission : null}
                            onChange={(fieldValue) => updateField('commission', fieldValue ?? 0)}
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">滑点</div>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={0.01}
                            suffix="%"
                            value={Number.isFinite(formData.slippage) ? formData.slippage : null}
                            onChange={(fieldValue) => updateField('slippage', fieldValue ?? 0)}
                        />
                    </div>
                    <div className="second-step__field">
                        <div className="second-step__label">仓位分配</div>
                        <Select
                            value={formData.position_sizing}
                            onChange={(fieldValue) => updateField('position_sizing', fieldValue)}
                            options={[
                                { value: '等权分配', label: '等权分配' },
                                { value: '风险平价', label: '风险平价' },
                                { value: '波动率倒数', label: '波动率倒数' },
                            ]}
                        />
                    </div>
                </div>
            </section>
            <section className="second-step__section">
                <div className="second-step__section-title font-weight-600">关注板块</div>
                <div className="operate-manager-page__tags">
                    {sectorOptions.map((sector) => (
                        <Tag.CheckableTag
                            key={sector}
                            checked={formData.focus_sectors.includes(sector)? true : false}
                            onChange={(checked) => toggleSector(sector, checked)}
                            className="border-tag text-11"
                        >
                            {sector}
                        </Tag.CheckableTag>
                    ))}
                </div>
            </section>

            <section className="second-step__section">
                <div className="second-step__section-title font-weight-600">风险约束</div>
                <div className="second-step__section__constraints grid gap-8">
                    {formData.constraints.map((item, index) => (
                        <div key={index} className="operate-manager-page__constraint-item flex flex-center">
                            <span className="operate-manager-page__constraint-dot" />
                            <Input
                                value={item}
                                onChange={(e) => updateConstraintDraft(e.target.value, index)}
                                onBlur={(e) => changeConstraint(e.target.value, index)}
                                placeholder="例如：单股持仓不超过20%、行业暴露不超过30%..."
                            />
                            <Button
                                type="text"
                                style={{ border: 'none' }}
                                icon={<CloseOutlined />}
                                onClick={() => removeConstraint(index)}
                            />
                        </div>
                    ))}
                </div>
                <Button icon={<PlusOutlined />} style={{ marginTop: '14px' }} onClick={addConstraint}>
                    添加约束条件
                </Button>
            </section>

            <section className="second-step__section">
                <div className="second-step__section-title font-weight-600">
                    选择股票池 <span style={{ color: 'var(--accent)' }}>*</span>
                </div>
                <div className="second-step__section__pool grid">
                    { formData.stock_pool_id !== null && Number(formData.stock_pool_id) > 0 && 
                        <Tag style={{ width: 'fit-content',padding: '6px 10px',textAlign: 'center',marginBottom: 10,borderColor: 'var(--accent)' }} color="blue">
                            {formData.stock_pool_name}
                        </Tag>
                    }
                    <Button style={{ width: 'fit-content' }} onClick={() => setVisible(true)}>
                        {formData.stock_pool_id ? '修改股票池' : '选择股票池'}
                    </Button>
                </div>
            </section>

            <section className="second-step__section">
                <div className="second-step__section-title font-weight-600">信号接入</div>
                <div className="second-step__switch-list">
                    {signalOptions.map((item) => (
                        <div key={item.key} className="second-step__switch-item">
                            <Switch
                                size='small'
                                checked={Boolean(formData.signals[item.key])}
                                onChange={(checked) => updateSignal(item.key, checked)}
                            />
                            <div className="second-step__switch-content">
                                <div className="second-step__switch-title font-weight-500">{item.title}</div>
                                <div className="second-step__switch-description">{item.description}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <ContactPool open={visible} onCancel={() => setVisible(false)} defaultStockPoolId={formData.stock_pool_id} onContactPool={onContactPool} />
        </section>
    )
}
